import React, { useCallback, useMemo, useState, useRef, useEffect } from 'react';
import { withWindow } from '@shared/utils/withWindow';
import { useTranslation } from '@shared/core';
import type { TranslationsByNamespace } from '@locales/generated';
import { DateFragment, EventType } from '@graphql/generated';
import { utcToZonedTime } from 'date-fns-tz';
import { format } from 'date-fns';
import { ReactComponent as IconRegistry } from '@assets/icons/icon-registry.svg';
import { ReactComponent as IconGear } from '@assets/icons/icon-gear.svg';
import { ReactComponent as IconAddGuest } from '@assets/icons/icon-add-guest.svg';
import { ReactComponent as IconHelp } from '@assets/icons/icon-help-default.svg';
import { ReactComponent as IconLEave } from '@assets/icons/icon-leave.svg';
import { IdentityWidgetProps } from './IdentityWidget.types';
import { Profile, Home } from '@withjoy/joykit/icons';
import { FeatureFlagsContextType, useFeatureFlagsContext } from '@shared/core/featureFlags';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { useResponsive } from '@shared/utils/hooks/useResponsive';
import { UserProfile } from '@shared/components/AuthProvider';
import { EventMembership } from '@shared/components/AuthProvider/AuthProvider.reducer';
import { AuthContextValue, useAuth } from '@shared/components/AuthProvider/AuthProvider.context';

type IdentityLinkCreator = {
  Icon: (props: { userProfile: UserProfile['profile'] }) => JSX.Element;
  label: (input: TranslationsByNamespace<string>['marcom']['identityWidget']) => string;
  shouldInclude: (input: { event: EventMembership['event']; profile: UserProfile['profile']; isBabyEvent: boolean; isMobile?: boolean }) => boolean;
  onClick: (input: { event: EventMembership['event']; provideLogout: AuthContextValue['provideLogout']; formatToUrl: FeatureFlagsContextType['formatToUrl'] }) => void;
};

const IDENTITY_LINKS: Array<IdentityLinkCreator> = [
  {
    Icon: () => <Home />,
    label: input => input.toDashboard,
    shouldInclude: ({ event, isBabyEvent }) => !!event && !isBabyEvent,
    onClick: ({ event }) => {
      if (event) {
        withWindow(global => {
          global.location.href = `${global.location.origin}/${event.website}/edit/dashboard`;
        });
      }
    },
  },
  {
    Icon: () => <IconRegistry />,
    label: input => input.toRegistry,
    shouldInclude: ({ event }) => !!event,
    onClick: ({ event, formatToUrl }) => {
      if (event) {
        withWindow(global => {
          global.location.href = formatToUrl(`${global.location.origin}/${event.website}/edit/registry`);
        });
      }
    },
  },
  {
    Icon: () => <IconGear />,
    label: input => input.toSettings,
    shouldInclude: ({ event, isBabyEvent }) => !!event && !isBabyEvent,
    onClick: ({ event }) => {
      if (event) {
        withWindow(global => {
          global.location.href = `${global.location.origin}/${event.website}/edit/settings`;
        });
      }
    },
  },
  {
    Icon: () => <Profile size={24} />,
    label: input => input.toManageAcct,
    shouldInclude: ({ profile }) => !!profile,
    onClick: ({ event }) => {
      withWindow(global => {
        global.location.href = `${global.location.origin}/me`;
      });
    },
  },
  {
    Icon: () => <IconAddGuest />,
    label: input => input.toInvite,
    shouldInclude: ({ event, isBabyEvent }) => !!event && !isBabyEvent,
    onClick: ({ event }) => {
      if (event) {
        withWindow(global => {
          global.location.href = `${global.location.origin}/${event.website}/edit/settings/account`;
        });
      }
    },
  },
  {
    Icon: () => <IconHelp />,
    label: input => input.toHelp,
    shouldInclude: ({ profile }) => !!profile,
    onClick: ship => {
      withWindow(global => {
        global.location.href = 'https://help.withjoy.com';
      });
    },
  },
  {
    Icon: () => <IconLEave />,
    label: input => input.signOut,
    shouldInclude: ({ profile, isMobile }) => !!profile && !isMobile,
    onClick: ({ event, provideLogout }) => {
      provideLogout({ eventId: event?.id });
    },
  },
];

const giveFocusToDepartmentNavBar = () => {
  withWindow(global => {
    const firstLink = global.document.querySelector('.meganav a') as HTMLAnchorElement | null;
    firstLink?.focus();
  });
};

export const useIdentityWidgetContent = (props: IdentityWidgetProps) => {
  const { eventMemberships, profile, eventId } = props;

  const [isMobile] = useResponsive({ values: { mobile: true, tablet: true, desktop: false } });
  const { formatToUrl } = useFeatureFlagsContext();
  const { provideLogout } = useAuth();

  const { t2 } = useTranslation('marcom');

  const identityWidgetTranslations = t2('identityWidget');

  const currentEventMembership = useMemo(() => {
    return eventMemberships.find(x => x.event?.id === eventId);
  }, [eventMemberships, eventId]);

  const actionsToRender = useMemo(() => {
    const event = currentEventMembership?.event;
    const isBabyEvent = currentEventMembership?.event?.info.eventType === EventType.babyRegistry;
    const items: Array<{ label: string; onClick: () => unknown; Icon: () => JSX.Element }> = [];
    IDENTITY_LINKS.forEach(creator => {
      if (creator.shouldInclude({ event, profile, isBabyEvent, isMobile })) {
        items.push({
          Icon: () => <creator.Icon userProfile={profile} />,
          label: creator.label(identityWidgetTranslations),
          onClick: () => creator.onClick({ event, provideLogout, formatToUrl }),
        });
      }
    });
    return items;
  }, [currentEventMembership, identityWidgetTranslations, provideLogout, profile, formatToUrl, isMobile]);

  const formatDate = useCallback((date: DateFragment) => {
    const dateWithTimezone = utcToZonedTime(date.milliseconds, date.timezone);
    return format(dateWithTimezone, 'MMMM d, yyyy');
  }, []);

  const handleOnAddPhotosClick = useCallback(() => {
    const event = currentEventMembership?.event;
    if (event) {
      withWindow(global => {
        global.location.href = `${global.location.origin}/${event.website}/edit/photos`;
      });
    }
  }, [currentEventMembership]);

  const handleOnLogut = useEventCallback(() => provideLogout({ eventId: currentEventMembership?.event?.id }));

  return {
    currentEventMembership,
    actionsToRender,
    formatDate,
    handleOnAddPhotosClick,
    identityWidgetTranslations,
    handleOnLogut,
    isMobile,
  } as const;
};

export const useIdentityWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const openHandler = (e: React.KeyboardEvent): void => {
    e.stopPropagation();
    if (e.key === ' ' || e.key === 'Enter') {
      setIsOpen(!isOpen);
    }
    if (e.key === 'Tab') {
      giveFocusToDepartmentNavBar();
    }
  };

  const popoverContentRef = useRef<HTMLDivElement>(null);
  const pillContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const giveFocusToPopoverContent = () => {
      const buttonCollection: Array<HTMLLIElement> = Array.from((popoverContentRef.current?.getElementsByTagName('li') || []) as Array<HTMLLIElement>);
      if (buttonCollection && isOpen) {
        const firstLink = buttonCollection[0].firstChild as Maybe<HTMLAnchorElement>;
        firstLink?.focus();
      }
    };
    setTimeout(giveFocusToPopoverContent, 300);
  }, [isOpen]);

  return { isOpen, setIsOpen, popoverContentRef, openHandler, pillContainerRef };
};
